import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  hideHeader?: boolean;
  maxWidth?: string;
  forceInteraction?: boolean; // Hide close affordances (no X, no overlay/ESC close)
}

export const Modal = ({ isOpen, onClose, title, children, className, hideHeader, maxWidth, forceInteraction = false }: ModalProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (!forceInteraction && !hideHeader && e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, hideHeader]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={!forceInteraction && !hideHeader ? onClose : undefined}
      />
      <div
        className={cn(
          "relative w-full transform rounded-none border-2 border-primary bg-background p-0 shadow-xl transition-all animate-in fade-in zoom-in-95 duration-200",
          maxWidth || "max-w-md",
          className
        )}
      >
        {!hideHeader && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <h3 className="text-lg font-semibold leading-none tracking-tight text-gray-800 font-body">
              {title}
            </h3>
            {!forceInteraction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 rounded-full hover:bg-gray-100 text-gray-800 hover:text-gray-800"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            )}
          </div>
        )}
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};








